import React, { useState, useRef, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa';
import { useLanguage } from '../i18n/LanguageContext';

/**
 * @param {'header' | 'shell' | 'topbar'} [variant]
 */
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

const LanguageSwitcher = ({ compact = false, variant = 'header' }) => {
  const { i18n, t } = useTranslation();
  const languageCtx = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const listId = useId();

  const languages = LANGUAGES;

  const resolvedCode = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLanguage = languages.find((lang) => lang.code === resolvedCode) || languages[0];

  const handleLanguageChange = async (langCode, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setIsOpen(false);
    try {
      if (languageCtx?.changeLanguage) {
        await languageCtx.changeLanguage(langCode);
      } else {
        await i18n.changeLanguage(langCode);
        localStorage.setItem('preferredLanguage', langCode);
        sessionStorage.setItem('currentLanguage', langCode);
      }
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: langCode } }));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const idx = Math.max(0, languages.findIndex((l) => l.code === resolvedCode));
    setActiveIndex(idx);
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, resolvedCode, languages]);

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const onListKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % languages.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + languages.length) % languages.length);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(languages.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLanguageChange(languages[activeIndex].code, event);
    }
  };

  const isLightChrome = variant === 'shell' || variant === 'topbar';
  const openUp = variant === 'shell';

  const triggerClass = isLightChrome
    ? 'inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition-colors'
    : `flex items-center gap-1.5 rounded-lg text-white/95 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors ${
        compact ? 'px-2 py-1.5 text-xs' : 'px-2.5 py-1.5 text-sm'
      }`;

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-label={t('shell.chrome.languageCurrent', 'Language: {{name}}', { name: currentLanguage.name })}
        className={triggerClass}
      >
        <span className="text-[13px] leading-none" aria-hidden>{currentLanguage.flag}</span>
        <span className="uppercase tracking-wide text-[11px]">{currentLanguage.code}</span>
        <FaChevronDown className={`w-2 h-2 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />
      </button>

      {isOpen ? (
        <div
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={t('shell.chrome.chooseLanguage', 'Choose language')}
          onKeyDown={onListKeyDown}
          className={`absolute w-44 rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-[80] focus:outline-none ${
            openUp ? 'start-0 bottom-full mb-1.5' : 'end-0 top-full mt-1.5'
          }`}
        >
          {languages.map((lang, index) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={resolvedCode === lang.code}
              tabIndex={activeIndex === index ? 0 : -1}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={(e) => handleLanguageChange(lang.code, e)}
              className={`w-full px-3 py-2.5 text-start text-sm font-medium flex items-center gap-2 transition-colors focus-visible:outline-none focus-visible:bg-slate-100 dark:focus-visible:bg-slate-800 ${
                activeIndex === index ? 'bg-slate-50 dark:bg-slate-800' : ''
              } ${
                resolvedCode === lang.code
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              <span className="text-base leading-none" aria-hidden>{lang.flag}</span>
              <span className="flex-1 min-w-0 truncate">{lang.name}</span>
              {resolvedCode === lang.code ? (
                <span className="text-[10px] font-semibold uppercase text-blue-500">{t('shell.chrome.selected', 'On')}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LanguageSwitcher;
